import React from 'react';
import PropTypes from 'prop-types';
import Modal from '../../../../../components-new/ui/Modal';
import config from 'config';
import {getMemberRole} from 'utils/General';
import {
    inviteMemberWithParams,
    reInviteMember,
    updateMemberChatLimit,
    updateMemberCurrentChatLimit,
    updateMemberMainInfo
} from '../../../../../services/company/api';
import {t} from "../../../../../components/Translate";
import {handleValueChange} from "../../../../../utils/ComponentUtilFunctions";
import EmployeesTable from "./EmployeesTable";
import InviteEmployees from "./InviteEmployees";
import ConfirmationModal from "../../../../../components-new/modals/ConfirmationModal";
import uploadFileApi from "../../../../../utils/FileService";
import EN from "../../../../../assets/flags/EN";
import HY from "../../../../../assets/flags/HY";
import Tabs from "../../../../../components-new/ui/Tabs";
import {fromBackendDate} from '../../../../../utils/DateUtils';


Modal.setAppElement('#root');

const columnData = [
    {
        id: 'user', numeric: false, disablePadding: true, label: 'User',
    },
    {
        id: 'status', numeric: false, disablePadding: true, label: 'Status',
    },
    {
        id: 'currentStatus', numeric: false, disablePadding: true, label: 'currentStatus',
    },
    {
        id: 'role', numeric: false, disablePadding: true, label: 'Role',
    },
    {
        id: 'language', numeric: false, disablePadding: true, label: 'language',
    },
    {
        id: 'chatLimit', numeric: true, disablePadding: true, label: 'chatLimit',
    },
    {
        id: 'currentChatLimit', numeric: true, disablePadding: true, label: 'currentChatLimit',
    },
    {
        id: 'actions', numeric: false, disablePadding: true, label: '',
    },
];

function getSorting(order, orderBy) {
    return (a, b) => {
        const valA = a[orderBy];
        const valB = b[orderBy];

        const AisNull = valA == null;
        const BisNull = valB == null;

        if (AisNull && BisNull) return 0;

        if (order === 'asc') {
            if (AisNull) return -1;
            if (BisNull) return 1;
        }

        if (order === 'desc') {
            if (AisNull) return 1;
            if (BisNull) return -1;
        }

        const dateA = parseIfDate(valA);
        const dateB = parseIfDate(valB);
        const bothAreDates = dateA && dateB;

        if (bothAreDates) {
            return order === 'desc'
                ? dateB - dateA
                : dateA - dateB;
        }

        if (typeof valA === 'number' && typeof valB === 'number') {
            return order === 'desc'
                ? valB - valA
                : valA - valB;
        }

        const strA = String(valA).toLowerCase();
        const strB = String(valB).toLowerCase();

        return order === 'desc'
            ? strB.localeCompare(strA)
            : strA.localeCompare(strB);
    };
}

function parseIfDate(value) {
    if (!(value instanceof Date) && typeof value !== 'string') return null;
    const date = fromBackendDate(value);
    return Number.isNaN(date.getTime()) ? null : date;
}

const styles = {
    /*
     * Full width, and as tall as what is left of the window: the page does not
     * scroll, the table inside it does. maxWidth 95% with auto margins used to
     * take another 2.5% off each side on top of the page's own inset.
     */
    /*
     * What makes the table scroll rather than the page - the same shape History
     * uses.
     *
     * Nothing from here down had a height, and the table inside asks for
     * whatever its parent has left over. The leftover of a box that has no
     * height is not a number, so the table simply grew to fit its rows: at fifty
     * to a page the tabs, the search box and the pagination scrolled away with
     * it and the scrollbar belonged to the whole content area.
     *
     * 100% rather than a calc - the content area around this already has a
     * resolved height, so none of this needs to know how tall the header and the
     * insets happen to be.
     */
    page: {
        display: 'flex',
        flexDirection: 'column',
        height: '100%',
        minHeight: 0,
        // only the table scrolls; the tabs stay at the top, the pagination at
        // the bottom
        overflow: 'hidden',
    },
    /*
     * A column, not a wrapping row - and that is the whole of this fix.
     *
     * It was display:flex with flex-wrap:wrap, which makes it a ROW: the height
     * of its one child then came from the flex line, and a wrapped line is sized
     * to its content first. align-content:stretch can grow a line to fill the
     * leftover space but never shrinks one, so a tall table kept the line tall,
     * the child stayed tall with it, and the pagination underneath was pushed
     * past the bottom of the page and clipped by its overflow:hidden.
     *
     * flex:1 1 auto was on the child all along, but in a row that governs width.
     * In a column it governs height, which is what was wanted - and is exactly
     * the shape History uses.
     */
    container: {
        display: 'flex',
        flexDirection: 'column',
        gap: '20px',
        flex: '1 1 auto',
        minHeight: 0,
    },
    wrapper: {
        width: '100%',
        boxShadow: 'none',
        overflowX: 'auto',
    },
    avatar: {
        float: 'left',
        marginRight: '20px',
        width: '30px',
        height: '30px',
    },
    userName: {
        fontWeight: '100',
        fontSize: '14px',
    },
    userEmail: {
        fontSize: '12px',
    },
};

let roles = config.roles.filter((c) => c.id !== config.rolesMap.owner);
let locales = config.localesOptions;

const iconMap = {
    hy: <HY height={18}/>,
    en: <EN height={18}/>
}

class Employees extends React.Component {
    constructor(props) {
        super(props);
        this.state = {
            searchString: '',
            selected: [],
            page: 0,
            rowsPerPage: 5,
            chatLimit: {},
            currentChatLimit: {},
            role: roles[0],
            username: '',
            email: '',
            firstName: '',
            lastName: '',
            password: '',
            thumbUrl: '',
            canChangeHimselfInfo: false,
            language: locales[0],
            activeTab: 'Members',
            isConfirmationDialogOpen: false,
            removingMembers: {},
            localFile: null,
            order: 'desc',
            orderBy: 'createdAt',
        };
        this.handleChange = handleValueChange.bind(this);
        this.setState = this.setState.bind(this);
    }

    getFilteredMembers = () => {
        const {searchString, selected} = this.state;
        const {members, user} = this.props;
        const lowerCaseSearchString = searchString.toLowerCase();
        return members.filter((member) => {
            if (member.id === user.id) return false;

            const firstName = member.firstName ? member.firstName.toLowerCase() : '';
            const lastName = member.lastName ? member.lastName.toLowerCase() : '';
            const email = member.email ? member.email.toLowerCase() : '';

            const fullName = `${firstName} ${lastName}`.toLowerCase();
            const searchStringMatches =
                fullName.includes(lowerCaseSearchString) ||
                email.includes(searchString.toLowerCase());

            if (searchStringMatches) {
                return true;
            }

            return selected.indexOf(member.id) !== -1;
        });
    };

    getSelectedMembers = () => {
        const {members} = this.props;
        const {selected} = this.state;
        return members.filter(member => selected.indexOf(member.id) !== -1);
    };

    handleSelectAllClick = (checked) => {
        const {order, orderBy, page, rowsPerPage} = this.state
        const currentSelected = this.state.selected

        const filteredMembers = this.getFilteredMembers()
        const pageData = filteredMembers.sort(getSorting(order, orderBy))
            .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);

        if (checked) {
            this.setState({
                selected: [
                    ...currentSelected,
                    ...pageData.filter(n => !currentSelected.includes(n.id) && n.role !== config.rolesMap.owner).map(n => n.id),
                ]
            });

            return;
        }

        this.setState({selected: currentSelected.filter(n => !pageData.some(m => m.id === n))});
    };

    handleMemberSelectChange = id => (isSelected) => {
        const {selected} = this.state;

        if (isSelected) {
            this.setState({selected: [id, ...selected]});
        } else {
            this.setState({selected: selected.filter(curId => curId !== id)});
        }
    };

    handleChangePage = (page) => {
        this.setState({page});
    };

    handleChangeRowsPerPage = (value) => {
        this.setState({rowsPerPage: value, page: 0});
    };

    handleSearch = (searchString) => {
        this.handleChangePage(0)
        this.setState({
            searchString,
        });
    };

    isSelected = (id) => {
        const {selected} = this.state;
        return selected.indexOf(id) !== -1;
    };

    handleHimselfInfoCheckboxChange = () => {
        this.setState((prevState) => ({
            canChangeHimselfInfo: !prevState.canChangeHimselfInfo
        }));
    };

    handleMemberStatusChange = member => (statusId) => {
        if (statusId === member.status) {
            return;
        }
        const {updateMember} = this.props;

        const memberUpdateInfo = {id: member.id, status: statusId, isStatus: true};
        updateMember(memberUpdateInfo);
    };

    handleMemberCurrentStatusChange = member => (statusId) => {
        const {updateMember} = this.props;
        const memberUpdateInfo = {id: member.id, currentStatus: statusId, isCurrentStatus: true}
        updateMember(memberUpdateInfo)
    };

    handleMemberRoleChange = member => (roleId) => {
        if (getMemberRole(member).id !== roleId) {
            const {updateMember} = this.props;
            const {roles} = config;
            const newRole = roles.find(role => role.id === roleId);
            const memberUpdateInfo = {id: member.id, authorities: [newRole.id]}
            updateMember(memberUpdateInfo);
        }
    };

    handleMemberCCHI = member => (canChangeHimselfInfo) => {
        const {updateMember} = this.props;
        const memberUpdateInfo = {id: member.id, canChangeHimselfInfo: canChangeHimselfInfo}
        updateMember(memberUpdateInfo)
    };

    handleRemoveMembers = () => {
        const {removeMembers} = this.props;
        removeMembers(this.state.removingMembers);
        this.closeConfirmationDialog();
    };

    openConfirmationDialog = (members) => {
        this.setState({isConfirmationDialogOpen: true})
        this.setState({removingMembers: members})
    }

    closeConfirmationDialog = () => {
        this.setState({isConfirmationDialogOpen: false})
        this.setState({removingMembers: {}})
    }

    reInviteMember = (memberInfo) => {
        const {addNotification} = this.props;
        reInviteMember(memberInfo.id).then(() => {
            addNotification('memberInvited');
        }).catch(() => {
            addNotification('somethingWentWrong', config.notificationLevels.error);
        });
    };

    handleChatLimitChange = (platformId) => (event) => {
        const chatLimitTarget = event.target.value;
        this.setState(prevState => ({
            chatLimit: {
                ...prevState.chatLimit,
                [platformId]: chatLimitTarget,
            }
        }));
    };

    handleCurrentChatLimitChange = (platformId) => (event) => {
        const currentChatLimitTarget = event.target.value;
        this.setState(prevState => ({
            currentChatLimit: {
                ...prevState.currentChatLimit,
                [platformId]: currentChatLimitTarget,
            }
        }));
    };

    handleChatLimitBlur = (platformId) => () => {
        const {chatLimit} = this.state;
        const chatLimitValue = chatLimit[platformId];

        if (chatLimitValue !== undefined && chatLimitValue !== '') {
            updateMemberChatLimit(platformId, chatLimitValue)
                .then(() => {
                    this.props.addNotification('userInfoUpdated', config.notificationLevels.success);
                })
                .catch(() => {
                    this.props.addNotification('somethingWentWrong', config.notificationLevels.error);
                });
        }
    };

    handleCurrentChatLimitBlur = (platformId) => () => {
        const {currentChatLimit} = this.state;
        const currentChatLimitValue = currentChatLimit[platformId];

        if (currentChatLimitValue !== undefined && currentChatLimitValue !== '') {
            updateMemberCurrentChatLimit(platformId, currentChatLimitValue)
                .then(() => {
                    this.props.addNotification('userInfoUpdated', config.notificationLevels.success);
                })
                .catch(() => {
                    this.props.addNotification('somethingWentWrong', config.notificationLevels.error);
                });
        }
    };

    handleAddUser = async () => {
        const {
            email,
            username,
            firstName,
            lastName,
            role,
            language,
            password,
            thumbUrl,
            canChangeHimselfInfo,
            localFile
        } = this.state;
        const {addNotification} = this.props;

        if (!email || !username || !firstName || !lastName || !password) {
            addNotification('validation.field_empty', config.notificationLevels.error);
            return;
        }

        let uploadedUrl = thumbUrl;

        if (localFile instanceof File) {
            try {
                const formData = new FormData();
                formData.append('file', localFile);

                uploadedUrl = await uploadFileApi(formData);

            } catch (e) {
                addNotification('somethingWentWrong', config.notificationLevels.error);
                return;
            }
        }

        try {
            await inviteMemberWithParams({
                email,
                username,
                firstName,
                lastName,
                role: role.value || role,
                language: language.value || language,
                password,
                thumbUrl: uploadedUrl,
                canChangeHimselfInfo,
            });

            this.setState({
                role: roles[0],
                username: '',
                email: '',
                firstName: '',
                lastName: '',
                password: '',
                thumbUrl: '',
                localFile: null,
                canChangeHimselfInfo: false,
                language: locales[0],
            });

            addNotification('memberAdded');
            this.props.fetchCompany();
        } catch (e) {
            /*
             * The server names what went wrong - operator.limit_exceeded,
             * user.duplicate_email - and the toast looks the name up in the
             * locales. Anything without a name, a dropped connection included,
             * falls back to the general line.
             */
            const reason = e.response && e.response.data;
            addNotification(reason || 'somethingWentWrong', config.notificationLevels.error);
        }
    };

    handleThumbUrlDelete = () => {
        this.setState({thumbUrl: ""});
    }

    handleOperatorInfoChange = (userId, field, newValue) => {
        const {members} = this.props;
        const trimmedValue = newValue.trim();
        if (!trimmedValue && field !== "thumbUrl") return;

        const originalUser = members.find(m => m.id === userId);
        if (!originalUser) return;

        if (originalUser[field] === trimmedValue) {
            return;
        }

        let data;
        if (field === 'firstName') {
            data = {
                firstName: newValue,
                lastName: originalUser.lastName,
                language: originalUser.language,
                contactNumber: '',
                thumbUrl: originalUser.thumbUrl
            }
        } else if (field === 'lastName') {
            data = {
                firstName: originalUser.firstName,
                lastName: newValue,
                language: originalUser.language,
                contactNumber: '',
                thumbUrl: originalUser.thumbUrl
            }
        } else if (field === 'thumbUrl') {
            data = {
                firstName: originalUser.firstName,
                lastName: originalUser.lastName,
                language: originalUser.language,
                contactNumber: '',
                thumbUrl: newValue
            }
        } else if (field === 'language') {
            data = {
                firstName: originalUser.firstName,
                lastName: originalUser.lastName,
                language: newValue,
                contactNumber: '',
                thumbUrl: originalUser.thumbUrl
            }
        }

        updateMemberMainInfo(data, userId)
            .then(() => {

                if (this.props.fetchCompany) {
                    this.props.fetchCompany();
                }

                this.props.addNotification('userInfoUpdated', config.notificationLevels.success);
            })
            .catch(() => {
                this.props.addNotification('somethingWentWrong', config.notificationLevels.error);
            });
    };

    handleOperatorProfileChange = (userId, data) => {
        return updateMemberMainInfo(data, userId)
            .then(() => {
                if (this.props.fetchCompany) {
                    this.props.fetchCompany();
                }
                this.props.addNotification('userInfoUpdated', config.notificationLevels.success);
            })
            .catch((error) => {
                this.props.addNotification('somethingWentWrong', config.notificationLevels.error);
                throw error;
            });
    };

    setOrder(orderBy, order) {
        this.setState({
            order,
            orderBy,
        })
    }

    render() {
        const {
            user,
            company,
            addNotification,
        } = this.props;
        const {
            order, orderBy, rowsPerPage, page, searchString, chatLimit, currentChatLimit,
            email, username, firstName, lastName, role, language, password, thumbUrl, canChangeHimselfInfo, activeTab,
            isConfirmationDialogOpen
        } = this.state;

        const features = company.tariff.features

        const filteredMembers = this.getFilteredMembers()
        const pageData = filteredMembers.sort(getSorting(order, orderBy))
            .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);
        let numSelected = 0;
        filteredMembers.forEach((member) => {
            numSelected += this.isSelected(member.id);
        });
        const selectedMembers = this.getSelectedMembers();

        return (
            <div className={'fade-in'} style={styles.page}>
                <div style={{marginBottom: '20px', flexShrink: 0}}>

                    <Tabs
                        selectedTab={activeTab}
                        onTabChange={tab => this.setState({activeTab: tab})}
                        tabs={[
                            'Members', 'inviteOrAddMembers',
                        ]}
                    />

                </div>

                <div style={styles.container}>
                    {activeTab === 'Members' && (
                        <div style={{
                            width: '100%',
                            flex: '1 1 auto',
                            minHeight: 0,
                            display: 'flex',
                            flexDirection: 'column'
                        }}>
                            <EmployeesTable
                                numSelected={numSelected}
                                selectedMembers={selectedMembers}
                                searchString={searchString}
                                handleSearch={this.handleSearch}
                                handleRemoveMembers={this.openConfirmationDialog}
                                user={user}
                                columnData={columnData}
                                order={order}
                                orderBy={orderBy}
                                setOrder={(orderBy, order) => this.setOrder(orderBy, order)}
                                filteredMembers={filteredMembers}
                                pageData={pageData}
                                chatLimit={chatLimit}
                                currentChatLimit={currentChatLimit}
                                rowsPerPage={rowsPerPage}
                                page={page}
                                handleSelectAllClick={this.handleSelectAllClick}
                                isSelected={this.isSelected}
                                iconMap={iconMap}
                                handleMemberSelectChange={this.handleMemberSelectChange}
                                handleOperatorInfoChange={this.handleOperatorInfoChange}
                                handleOperatorProfileChange={this.handleOperatorProfileChange}
                                handleMemberStatusChange={this.handleMemberStatusChange}
                                handleMemberCurrentStatusChange={this.handleMemberCurrentStatusChange}
                                handleMemberRoleChange={this.handleMemberRoleChange}
                                handleMemberCCHI={this.handleMemberCCHI}
                                handleChatLimitChange={this.handleChatLimitChange}
                                handleChatLimitBlur={this.handleChatLimitBlur}
                                handleCurrentChatLimitChange={this.handleCurrentChatLimitChange}
                                handleCurrentChatLimitBlur={this.handleCurrentChatLimitBlur}
                                reInviteMember={this.reInviteMember}
                                handleChangeRowsPerPage={this.handleChangeRowsPerPage}
                                handleChangePage={this.handleChangePage}
                            />
                        </div>
                    )}

                    {/*
                      * The form scrolls, unlike the table beside it.
                      *
                      * The page is a column with overflow hidden so that the
                      * members table keeps its scrollbar to itself - which left
                      * this tab with no way out at all, and on a short window the
                      * foot of the form was simply unreachable. A table has a
                      * viewport of its own to scroll inside; a form is just tall,
                      * so this one takes the page's leftover height and scrolls
                      * within it.
                      */}
                    {activeTab === 'inviteOrAddMembers' && (
                        <div style={{
                            width: '100%',
                            flex: '1 1 auto',
                            minHeight: 0,
                            overflowY: 'auto',
                            /* room for the scrollbar so it does not sit on the
                               form controls */
                            paddingRight: '4px'
                        }}>
                            <InviteEmployees
                                localFile={this.state.localFile}
                                setState={this.setState}
                                user={user}
                                company={company}
                                addNotification={addNotification}
                                fetchCompany={this.props.fetchCompany}
                                email={email}
                                username={username}
                                firstName={firstName}
                                lastName={lastName}
                                password={password}
                                thumbUrl={thumbUrl}
                                role={role}
                                language={language}
                                roles={roles}
                                locales={locales}
                                iconMap={iconMap}
                                canChangeHimselfInfo={canChangeHimselfInfo}
                                handleChange={this.handleChange}
                                handleThumbUrlDelete={this.handleThumbUrlDelete}
                                handleAddUser={this.handleAddUser}
                                handleHimselfInfoCheckboxChange={this.handleHimselfInfoCheckboxChange}
                            />
                        </div>
                    )}

                    {isConfirmationDialogOpen &&
                        <ConfirmationModal
                            variant="delete"
                            isOpen={isConfirmationDialogOpen}
                            onClose={() => this.setState({isConfirmationDialogOpen: false})}
                            onConfirm={this.handleRemoveMembers}
                            message={t({
                                text: 'areYouSureYouWantToProceed',
                                user
                            })}
                        />
                    }

                </div>
            </div>
        );
    }
}

Employees.propTypes = {
    user: PropTypes.object.isRequired,
    members: PropTypes.array.isRequired,
    updateMember: PropTypes.func.isRequired,
    removeMembers: PropTypes.func.isRequired,
    addNotification: PropTypes.func.isRequired,
};

export default Employees



// WEBPACK FOOTER //
// ./src/scenes/dashboard/components/management/members/Employees.jsx